import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import LoadingButton from '@mui/lab/LoadingButton'
import { Alert, Dialog, DialogContent, Button, Box, AlertTitle, useTheme } from '@mui/material'
import HHDialogActions from 'components/common/HHDialogActions'
import { noop } from 'lodash'
import T from 'T'
import { PAYMENT_STATUS, PAYMENT_METHODS } from 'settings/constants/payment'
import { get } from 'utils/lodash'
import { downloadInvoiceBlobListInChunks, downloadPaymentReceiptBlob, checkPaymentReceiptPDFExists } from 'utils/file'
import { useLazyGetTransactionListQuery } from 'api/payment-gateway/paymentGateway'

const { ACCOUNT_CREDIT } = PAYMENT_METHODS
const { APPROVED } = PAYMENT_STATUS
const { PENDING } = PAYMENT_STATUS

const POLL_INTERVAL_MS = 5000
const MAX_WAIT_MS = 25000

const PaymentStatusDialog = ({
  isOpen = false,
  onClose = noop,
  isMakingPayment,
  isPaymentSuccessful,
  hasPaymentError,
  isChargedByFortis,
  transaction = {},
  isACH,
  businessId,
  accountId: accountIdProp,
}) => {
  const theme = useTheme()
  // Prefer accountId from props (selector) when missing in transaction
  const { accountId, accountTransactionHistoryId: txnATHId, paymentNumber: txnPaymentNumber, transactionRef, batchId } = transaction

  const effectiveAccountId = useMemo(() => accountId || accountIdProp, [accountId, accountIdProp])

  // Fetch transactions and merge matching one for enriched data
  const [getTransactionList] = useLazyGetTransactionListQuery()
  const [matchedTransaction, setMatchedTransaction] = useState(null)

  useEffect(() => {
    let cancelled = false
    const shouldFetch = Boolean(isOpen && businessId && effectiveAccountId)
    if (!shouldFetch) return undefined

    getTransactionList({ businessId, payload: { accountId: effectiveAccountId, requestedPage: 0, requestedPageSize: 25 } })
      .unwrap()
      .then(data => {
        if (cancelled) return
        const list = get(data, 'transactions', [])
        const matchByPayment = txnPaymentNumber ? list.find(t => t.paymentNumber === txnPaymentNumber) : null
        const matchByATH = !matchByPayment && txnATHId ? list.find(t => t.accountTransactionHistoryId === txnATHId) : null
        const matchByTxnRef = !matchByPayment && !matchByATH && transactionRef ? list.find(t => t.transactionRef === transactionRef) : null
        const matchByBatchId = !matchByPayment && !matchByATH && !matchByTxnRef && batchId ? list.find(t => t.batchId === batchId) : null
        setMatchedTransaction(matchByPayment || matchByATH || matchByTxnRef || matchByBatchId || null)
      })
      .catch(() => {
        if (cancelled) return
        setMatchedTransaction(null)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, businessId, effectiveAccountId, txnPaymentNumber, txnATHId, transactionRef, batchId, getTransactionList])

  const mergedTransaction = useMemo(() => ({ ...matchedTransaction, ...(transaction || {}) }), [transaction, matchedTransaction])

  const { accountTransactionHistoryId, paymentNumber, transactionStatus } = mergedTransaction
  const isPaymentApproved = useMemo(() => transactionStatus === APPROVED, [transactionStatus])
  // Fortis charged before the record call, so that call failing does not mean the payment failed.
  const isPaymentPending = useMemo(
    () => transactionStatus === PENDING || (hasPaymentError && isChargedByFortis),
    [transactionStatus, hasPaymentError, isChargedByFortis]
  )

  const [isPaymentReceiptGenerated, setIsPaymentReceiptGenerated] = useState(false)

  const canDownloadReceipt = useMemo(
    () => Boolean(businessId && effectiveAccountId && accountTransactionHistoryId),
    [businessId, effectiveAccountId, accountTransactionHistoryId]
  )

  const receiptFileName = useMemo(
    () =>
      paymentNumber ||
      get(mergedTransaction, 'transactionRef') ||
      get(mergedTransaction, 'batchId') ||
      accountTransactionHistoryId ||
      'payment-receipt',
    [paymentNumber, mergedTransaction, accountTransactionHistoryId]
  )

  const invoiceNumberList = useMemo(
    () => get(mergedTransaction, 'paymentTarget', []).filter(target => target !== ACCOUNT_CREDIT),
    [mergedTransaction]
  )

  const handleDownloadInvoices = () => downloadInvoiceBlobListInChunks(invoiceNumberList, businessId, true)

  const handleDownloadPaymentReceipt = () => {
    if (!canDownloadReceipt) return
    downloadPaymentReceiptBlob(businessId, effectiveAccountId, receiptFileName, accountTransactionHistoryId, true)
  }

  useEffect(() => {
    let cancelled = false

    const hasReqs = Boolean(isOpen && businessId && effectiveAccountId && accountTransactionHistoryId)
    if (hasReqs) {
      const start = Date.now()

      const check = async () => {
        const exists = await checkPaymentReceiptPDFExists(businessId, effectiveAccountId, accountTransactionHistoryId)
        if (cancelled) return

        if (exists) {
          setIsPaymentReceiptGenerated(true)
        } else {
          const elapsed = Date.now() - start
          if (elapsed >= MAX_WAIT_MS) {
            setIsPaymentReceiptGenerated(true)
          } else {
            setTimeout(check, POLL_INTERVAL_MS)
          }
        }
      }

      setIsPaymentReceiptGenerated(false)
      check()
    } else {
      // If prerequisites for polling aren't met, avoid showing spinner
      setIsPaymentReceiptGenerated(true)
    }

    return () => {
      cancelled = true
    }
  }, [isOpen, businessId, effectiveAccountId, accountTransactionHistoryId])

  return (
    <Dialog open={isOpen} maxWidth="md" onClose={onClose}>
      <DialogContent>
        {isPaymentSuccessful && isPaymentApproved && !isMakingPayment && (
          <Alert
            severity="success"
            sx={{
              ...theme.typography.body1,
            }}
          >
            <AlertTitle>{`${T.PAYMENT_SUCCESS_TOP_MSG}`}</AlertTitle>
            {`${T.PAYMENT_SUCCESS_BOTTOM_MSG}`}
          </Alert>
        )}
        {isPaymentPending && !isMakingPayment && (
          <Alert
            severity="warning"
            sx={{
              ...theme.typography.body1,
            }}
          >
            <AlertTitle>{`${T.PAYMENT_PROCESSING_TOP_MSG}`}</AlertTitle>
            {`${T.PAYMENT_PROCESSING_BOTTOM_MSG}`}
          </Alert>
        )}
        {isMakingPayment && (
          <Alert
            severity="info"
            sx={{
              ...theme.typography.body1,
            }}
          >
            <AlertTitle>{`${T.PAYMENT_PENDING_TOP_MSG} ${isACH ? 'ACH' : 'credit card'}`}</AlertTitle>
            {`${T.PAYMENT_PENDING_BOTTOM_MSG} ${T.THANK_YOU_FOR_PATIENCE}`}
          </Alert>
        )}
        {(hasPaymentError || !isPaymentApproved) && !isMakingPayment && !isPaymentPending && (
          <Alert
            severity="error"
            sx={{
              ...theme.typography.body1,
            }}
          >
            <AlertTitle>{`${T.PAYMENT_FAILED_TOP_MSG}`}</AlertTitle>
            {T.PAYMENT_FAILED_BOTTOM_MSG}
          </Alert>
        )}
      </DialogContent>
      <HHDialogActions>
        {invoiceNumberList.length > 0 && (
          <Button sx={{ '&:hover': { color: 'primary.main' } }} variant="outlined" onClick={handleDownloadInvoices}>
            Download invoice
          </Button>
        )}
        <Box sx={{ flex: '1 1 auto' }} />
        <LoadingButton
          loading={!isPaymentReceiptGenerated && canDownloadReceipt}
          disabled={!canDownloadReceipt}
          variant="contained"
          onClick={handleDownloadPaymentReceipt}
        >
          Download receipt
        </LoadingButton>
      </HHDialogActions>
    </Dialog>
  )
}

PaymentStatusDialog.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  isMakingPayment: PropTypes.bool,
  isPaymentSuccessful: PropTypes.bool,
  hasPaymentError: PropTypes.bool,
  isChargedByFortis: PropTypes.bool,
  transaction: PropTypes.object,
  isACH: PropTypes.bool,
  businessId: PropTypes.string,
  accountId: PropTypes.string,
}

export default PaymentStatusDialog
